import React, { useState, useRef, useEffect } from "react"
import styled from "@emotion/styled"
import * as layouts from "layouts"
import * as authzc from "authzcached"
import * as feedback from "brandguard/api/feedback"

const ExportButton = styled(layouts.buttons.outline)`
	font-size: 12px;
	padding: 8px 16px;
	display: flex;
	align-items: center;
	gap: 6px;
`

const DropdownContainer = styled(layouts.containers.flex)`
	position: relative;
`

const DropdownMenu = styled(layouts.containers.flex)`
	position: absolute;
	top: 100%;
	right: 0;
	margin-top: 4px;
	background: ${layouts.theme.colors.white};
	border: 1px solid ${layouts.theme.colors.grey.light10};
	border-radius: 4px;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
	z-index: 100;
	flex-direction: column;
	min-width: 140px;
`

const DropdownItem = styled(layouts.buttons.unstyled)`
	padding: 8px 12px;
	text-align: left;
	font-size: 12px;
	color: ${layouts.theme.colors.grey.dark50};
	background: transparent;
	cursor: pointer;
	&:hover {
		background: ${layouts.theme.colors.grey.lightest};
	}
`

interface props {
	eventType: "image" | "text" | "document"
	eventId: string
	onEmail?: () => void
}

export function FeedbackExportButton(props: props): JSX.Element {
	const { eventType, eventId, onEmail } = props
	const authzaccount = authzc.useCache((cached) => cached.meta)
	const [open, setOpen] = useState(false)
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState<string | null>(null)

	const handleExport = async (format: feedback.ExportFormat) => {
		setLoading(true)
		setOpen(false)
		setError(null)
		try {
			const resp = await feedback.exportFeedback(eventType, eventId, format, authzc.bearer(authzaccount))
			if (format === "json") {
				const blob = new Blob([JSON.stringify(resp, null, 2)], { type: "application/json" })
				downloadBlob(blob, `feedback-${eventId}.json`)
			} else {
				const blob = new Blob([resp.formatted_output], { type: "text/plain" })
				const ext = format === "markdown" ? "md" : "txt"
				downloadBlob(blob, `feedback-${eventId}.${ext}`)
			}
		} catch (err) {
			console.error("failed to export feedback", err)
			setError("Export failed. Please try again.")
		} finally {
			setLoading(false)
		}
	}

	const downloadBlob = (blob: Blob, filename: string) => {
		const url = URL.createObjectURL(blob)
		const a = document.createElement("a")
		a.href = url
		a.download = filename
		a.click()
		URL.revokeObjectURL(url)
	}

	return (
		<DropdownContainer>
			<ExportButton onClick={() => setOpen(!open)} disabled={loading}>
				{loading ? "Exporting..." : error ? "Export Failed" : "Export Feedback"}
			</ExportButton>
			{open && (
				<DropdownMenu>
					<DropdownItem onClick={() => handleExport("json")}>JSON</DropdownItem>
					<DropdownItem onClick={() => handleExport("markdown")}>Markdown</DropdownItem>
					<DropdownItem onClick={() => handleExport("text")}>Plain Text</DropdownItem>
					{onEmail && <DropdownItem onClick={onEmail}>Send via Email</DropdownItem>}
				</DropdownMenu>
			)}
		</DropdownContainer>
	)
}
